/**
 * ContactPortalPage — magic link entry point for contacts.
 * Route: /c/:token
 *
 * Flow:
 *   1. Extract :token from URL
 *   2. POST /api/auth/request-otp → triggers SMS delivery, returns masked phone
 *   3. Show OTPVerifyScreen (seeded with the masked phone so the patient knows
 *      where the code was sent). OTPVerifyScreen's module-level guard prevents a
 *      duplicate send when it mounts.
 *   4. On successful OTP verification → contact JWT is stored in AuthContext
 *   5. Route to the correct view based on entityType:
 *      - 'message' / 'message_access' → SecureMessageView
 *      - 'form_assignment'            → AssignedFormView
 *      - 'package_run'                → PackageRunView
 *
 * No sensitive data is ever placed in the URL or browser history.
 */

import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../../lib/auth-context.js';
import OTPVerifyScreen from './OTPVerifyScreen.js';
import SecureMessageView from './SecureMessageView.js';
import AssignedFormView from './AssignedFormView.js';
import PackageRunView from './PackageRunView.js';

type Screen =
  | { type: 'otp'; maskedPhone?: string }
  | { type: 'message'; entityId: string }
  | { type: 'form'; entityId: string }
  | { type: 'package_run'; entityId: string }
  | { type: 'error'; message: string };

export default function ContactPortalPage(): React.ReactElement {
  const { token } = useParams<{ token: string }>();
  const { isContact, user } = useAuth();

  // If the contact is already authenticated (e.g. soft-reload), we have lost the
  // entity context that the original magic link carried. Guide them back to the
  // link rather than leaving them stuck on a generic error.
  const [screen, setScreen] = useState<Screen>(() => {
    if (isContact && user?.role === 'contact') {
      return {
        type: 'error',
        message: 'Your secure session is still active, but this page needs the original link to know what to open. Please tap the link in your text message or email again to continue.',
      };
    }
    if (!token) {
      return { type: 'error', message: 'This link looks incomplete. Please open the secure link from your text message or email again.' };
    }
    return { type: 'otp' };
  });

  // OTP issuance is owned solely by OTPVerifyScreen (single source of truth), so a
  // page-level request can no longer cause a second code to be sent. The verify
  // screen captures the masked phone from its own request response.

  const handleOTPSuccess = (entityType: string, entityId: string) => {
    if (entityType === 'message' || entityType === 'message_access') {
      setScreen({ type: 'message', entityId });
    } else if (entityType === 'form_assignment') {
      setScreen({ type: 'form', entityId });
    } else if (entityType === 'package_run') {
      setScreen({ type: 'package_run', entityId });
    } else {
      setScreen({ type: 'error', message: 'Unknown content type. Please contact support.' });
    }
  };

  if (!token) {
    return <ErrorPage message="This link looks incomplete. Please open the secure link from your text message or email again." />;
  }

  switch (screen.type) {
    case 'otp':
      return (
        <OTPVerifyScreen
          magicToken={token}
          maskedPhone={screen.maskedPhone}
          onSuccess={handleOTPSuccess}
        />
      );

    case 'message':
      return <SecureMessageView messageId={screen.entityId} />;

    case 'form':
      return <AssignedFormView assignmentId={screen.entityId} />;

    case 'package_run':
      return <PackageRunView runId={screen.entityId} />;

    case 'error':
      return <ErrorPage message={screen.message} />;
  }
}

// ---------------------------------------------------------------------------
// Inline placeholder components
// ---------------------------------------------------------------------------

function ErrorPage({ message }: { message: string }): React.ReactElement {
  return (
    <div className="ci-portal" style={styles.fullCenter}>
      <div style={styles.card}>
        <span style={styles.errorIcon} aria-hidden="true">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#c0392b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
            <line x1="12" y1="9" x2="12" y2="13"/>
            <line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
        </span>
        <h2 style={styles.errorTitle}>Something went wrong</h2>
        <p style={styles.errorText}>{message}</p>
        <p style={styles.errorFooter}>
          If you believe this is an error, please contact the office directly.
        </p>
      </div>
    </div>
  );
}


const F = 'var(--ci-font)';

const styles: Record<string, React.CSSProperties> = {
  fullCenter: {
    minHeight:      '100vh',
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    background:     'var(--ci-surface)',
    padding:        '24px 16px',
    fontFamily:     F,
  },
  card: {
    background:   '#ffffff',
    borderRadius: 16,
    padding:      '40px 32px',
    maxWidth:     400,
    width:        '100%',
    textAlign:    'center',
    border:       '1px solid var(--ci-border)',
    fontFamily:   F,
  },
  errorIcon:   { display: 'inline-flex', alignItems: 'center', justifyContent: 'center' },
  errorTitle:  { margin: '16px 0 8px', fontSize: 18, fontWeight: 700, color: 'var(--ci-text)', fontFamily: F },
  errorText:   { margin: '0 0 16px', fontSize: 14, color: 'var(--ci-text-muted)', lineHeight: 1.6, fontFamily: F },
  errorFooter: { margin: 0, fontSize: 12, color: 'var(--ci-text-muted)', fontFamily: F },
};
